import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { Camera, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LogInstruction() {
  const [props_, setProps] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null);
  const [mode, setMode] = useState("next_visit");
  const [step, setStep] = useState("form"); // form | questions | done
  const [instr, setInstr] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  useEffect(() => { api.get("/properties").then((r) => setProps(r.data)); }, []);

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setPhoto(r.result);
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (!propertyId || !description) return toast.error("Pick a property and describe the work");
    setBusy(true);
    try {
      const r = await api.post("/instructions", { property_id: propertyId, description, photo, mode });
      setInstr(r.data);
      setAnswers(r.data.ai_questions.map(() => ""));
      setStep("questions");
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  const submitAnswers = async () => {
    setBusy(true);
    try {
      await api.post(`/instructions/${instr.id}/answers`, { answers });
      setStep("done");
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <Layout title="Request extra work">
      {step === "form" && (
        <div className="space-y-6" data-testid="instruction-form">
          <section>
            <p className="label-caps mb-2">Which property?</p>
            <div className="space-y-2">
              {props_.map((p) => (
                <button key={p.id} data-testid={`pick-property-${p.id}`} onClick={() => setPropertyId(p.id)}
                  className={`w-full rounded-md border p-4 text-left font-semibold transition-colors ${propertyId === p.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>
                  {p.name} <span className="block text-xs font-normal opacity-80">{p.address}</span>
                </button>
              ))}
            </div>
          </section>
          <section>
            <p className="label-caps mb-2">What do you need done?</p>
            <textarea data-testid="instruction-description" className="field-input h-28 w-full py-3" value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Collect garbage left by guests, remove yard waste, trim back the hedges…" />
            <label className="mt-3 flex h-12 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border text-sm font-semibold text-muted-foreground" data-testid="instruction-photo-btn">
              <Camera size={18} weight="bold" /> {photo ? "Photo attached" : "Add a photo (optional)"}
              <input type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
            </label>
          </section>
          <section>
            <p className="label-caps mb-2">When?</p>
            <div className="flex gap-2">
              <button data-testid="mode-next-visit" onClick={() => setMode("next_visit")}
                className={`h-14 flex-1 rounded-md border text-sm font-bold ${mode === "next_visit" ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                With the next visit
              </button>
              <button data-testid="mode-standalone" onClick={() => setMode("standalone")}
                className={`h-14 flex-1 rounded-md border text-sm font-bold ${mode === "standalone" ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                It can't wait
              </button>
            </div>
          </section>
          <button data-testid="instruction-submit" onClick={submit} disabled={busy} className="btn-primary-big">
            {busy ? "Thinking…" : "Send request"}
          </button>
        </div>
      )}

      {step === "questions" && instr && (
        <div className="space-y-6" data-testid="clarifying-questions">
          <div className="card-flat flex items-start gap-3 p-4">
            <Sparkle size={24} weight="bold" className="mt-1 shrink-0 text-primary" />
            <p className="text-sm">A couple of quick questions so your operator knows exactly what you need:</p>
          </div>
          {instr.ai_questions.map((q, i) => (
            <div key={q}>
              <p className="mb-2 text-sm font-semibold" data-testid={`question-${i}`}>{q}</p>
              <input data-testid={`answer-${i}`} className="field-input w-full" value={answers[i]}
                onChange={(e) => setAnswers(answers.map((a, j) => (j === i ? e.target.value : a)))} placeholder="Your answer" />
            </div>
          ))}
          <button data-testid="answers-submit" onClick={submitAnswers} disabled={busy} className="btn-primary-big">
            {busy ? "Sending…" : "Send answers"}
          </button>
        </div>
      )}

      {step === "done" && (
        <div className="card-flat space-y-4 p-6 text-center" data-testid="request-sent">
          <p className="font-heading text-2xl font-bold text-primary">Request sent</p>
          <p className="text-sm text-muted-foreground">
            Your operator will review it and send you a quote. If it's covered by your pre-approval, it'll go straight onto the schedule.
          </p>
          <button data-testid="back-home-btn" onClick={() => nav("/owner")} className="btn-primary-big !h-14 !text-base">Back to home</button>
        </div>
      )}
    </Layout>
  );
}
